/**
 * Summary translation per **D-08** (forward) and **D-09** (backward) — the
 * block model. When a summary is scheduled to a new anchor (start in forward
 * mode, end in backward mode), every descendant translates by the same
 * `delta` so the project moves as a whole and internal relative offsets are
 * preserved.
 *
 *   delta = newStart - summary.anchorStartTs   (forward)
 *   delta = newEnd   - summary.anchorEndTs     (backward)
 *
 * For each descendant `d`:
 *   1. proposed = d.current{Start|End} + delta (shifted in d's own calendar).
 *   2. compute the opposite endpoint via d.calendar + duration.
 *   3. **Constraint precedence (option A, GS-1771 parity):** if `d` has a hard
 *      constraint, re-apply its constraint bounds (identical to asap.ts forward
 *      / alap.ts backward) so the leaf clamps back to HONOR the constraint
 *      instead of being dragged past it. A leaf scheduled directly through
 *      resolveAsap/resolveAlap already honors MSO/MFO/FNLT/SNLT/FNET/SNET; this
 *      makes a leaf that is a summary DESCENDANT behave the same. Unconstrained
 *      siblings still translate by the full `delta`, so the block-move promise
 *      holds for everything that CAN move. (This replaces the earlier "commit
 *      the violating date + emit a constraint-violation Conflict" rule — see
 *      the GS-1771 discussion: a hard pin wins over a move, matching v1 and
 *      MS Project, and is consistent with the direct-leaf path.)
 *   4. If d has a cross-summary scheduling-edge predecessor that would
 *      force a different date: write the translation date now; the fixed-
 *      point loop's next iteration re-evaluates the cross-edge.
 *
 * The summary's own dates are NOT updated here. Per D-07 they are re-derived
 * after the pass completes — `start = min(descendant starts)`,
 * `end = max(descendant ends)` — via `aggregateSummaryDates` (called from
 * `schedule_pass.ts` at the end of the pass).
 *
 * Lenient calendar default (per `04-` §11(A)): a descendant landing on its
 * own non-working time snaps to the next working slot in the requested
 * direction. Strict mode (`SchedulingConfig.strictCalendar: true`) ALSO
 * emits a `calendar-non-working` Conflict but still commits the snapped
 * date.
 */

import type { ScheduleReason, SnapDirection, NormalizedTaskID, TimeUnit, Calendar } from "../types";
import type { SchedulingGraph, SchedNode } from "../graph/types";
import type { PerNodePlan } from "./schedule_pass";
import { pushCalendarNonWorking, type ConflictAccumulator } from "./conflict";
import { applyConstraintLowerBound, applyConstraintUpperBound } from "../analysis/slack";

export interface TranslateInputs {
  readonly graph: SchedulingGraph;
  readonly summaryId: NormalizedTaskID;
  /**
   * The summary's CURRENT (children-derived) anchor — `effBounds.start` in
   * forward mode, `effBounds.end` in backward — and the new anchor it resolved
   * to. Each descendant keeps its WORKING-TIME offset from the old anchor (in
   * the descendant's OWN calendar) when re-applied from the new anchor. Both
   * come from the summary's live children, not the frozen snapshot, so the
   * shift preserves the positions descendants reached via their own scheduling
   * (internal links). For flat / same-calendar cases this reduces exactly to a
   * uniform wall-clock shift.
   */
  readonly oldAnchorTs: number;
  readonly newAnchorTs: number;
  readonly direction: "forward" | "backward";
  /** Mutable per-pass plan map — this function writes descendant entries. */
  readonly plan: Map<NormalizedTaskID, PerNodePlan>;
  readonly conflicts: ConflictAccumulator;
  /** Strict-calendar mode opts into calendar-non-working Conflict emission. */
  readonly strictCalendar: boolean;
}

/**
 * Apply the D-08 hard-rule translation to every descendant of the summary.
 */
export function applyTranslation(inputs: TranslateInputs): void {
  const { graph, summaryId, oldAnchorTs, newAnchorTs, direction, plan, conflicts, strictCalendar } = inputs;

  const summary = graph.nodes.get(summaryId);
  if (!summary) return;
  if (summary.kind !== "summary") return;

  const delta = newAnchorTs - oldAnchorTs;
  // Phase D Day 10 perf: short-circuit when the summary didn't move.
  // Iterating descendants to write identical plan entries is wasted work.
  if (delta === 0) return;

  // Iterate descendants via containment edges. ContainmentEdge has
  // {summary, descendant}; we only follow ones rooted at this summary.
  for (const c of graph.containment) {
    if (c.summary !== summaryId) continue;
    const d = graph.nodes.get(c.descendant);
    if (!d) continue;
    // Excluded descendants (D-05 drag target) stay at their anchor.
    if (d.excluded) {
      plan.set(d.id, {
        startTs: d.anchorStartTs,
        endTs: d.anchorEndTs,
        reason: { kind: "preferred" }
      });
      continue;
    }
    translateDescendant(d, oldAnchorTs, newAnchorTs, delta, direction, plan, conflicts, strictCalendar, summaryId);
  }
}

function translateDescendant(
  d: SchedNode,
  oldAnchorTs: number,
  newAnchorTs: number,
  delta: number,
  direction: SnapDirection,
  plan: Map<NormalizedTaskID, PerNodePlan>,
  conflicts: ConflictAccumulator,
  strictCalendar: boolean,
  summaryId: NormalizedTaskID
): void {
  // Shift from the descendant's CURRENT plan position — the date it reached
  // via its own asap/alap resolution (which honors internal scheduling links)
  // — not from the frozen snapshot anchor. Using the anchor here was the
  // GS-1689 + GS-2427 multi-level bug.
  const current = plan.get(d.id);
  const baseStartTs = current ? current.startTs : d.anchorStartTs;
  const baseEndTs = current ? current.endTs : d.anchorEndTs;

  // Per-calendar working-time offset preservation (GS-2328). The descendant's
  // distance from the summary's old anchor, MEASURED IN ITS OWN CALENDAR, is
  // re-applied from the new anchor — so a subtask on a different calendar
  // lands on a working position relative to its own work schedule, not by a
  // wall-clock shift that may collapse or stretch non-working gaps. Nested
  // summaries fall back to wall-clock since they have no real calendar of
  // their own; `aggregateSummaryDates` re-derives them at end-of-pass anyway.
  // For flat / same-calendar descendants the working offset equals the
  // wall-clock offset, so this reduces to `base + delta` (no change to the
  // GS-1689 / GS-2427 fixes).
  // Choose the unit for the offset / re-add arithmetic. Using "minute" forces
  // every shifted endpoint to land on a working-time boundary, which
  // introduces a working-day-hour artifact when the base dates were aligned
  // to day boundaries (test 054 step 3 GS-`backwards_scheduling`: task 17
  // landed at `17-04 08:00` instead of `17-04 00:00`). Default duration_unit
  // (typically "day") preserves midnight alignment for the common case while
  // still being precise enough for multi-calendar translation (GS-2328) when
  // both endpoints share a calendar; finer-grained tasks rely on the unit
  // configured for the gantt instance.
  const offsetUnit = d.durationUnit;
  let proposedAnchorTs: number;
  if (d.kind === "summary") {
    proposedAnchorTs = direction === "forward" ? baseStartTs + delta : baseEndTs + delta;
  } else if (direction === "forward") {
    // The offset is SIGNED: since the movable-front refinements (GS-2675 /
    // GS-2875) `oldAnchorTs` is the block's movable front, NOT
    // `min(all descendant starts)` — a constraint-pinned or negative-lag
    // link-tethered descendant legitimately sits BEFORE it and must keep its
    // negative offset. Clamping it to 0 collapsed such a descendant onto the
    // new anchor (Task #4 at 29-03, front 02-04, landing 03-04: offset −4
    // must land it at 30-03, not at the landing itself).
    let offset = 0;
    if (baseStartTs > oldAnchorTs) {
      offset = d.calendar.calculateDuration(new Date(oldAnchorTs), new Date(baseStartTs), offsetUnit);
    } else if (baseStartTs < oldAnchorTs) {
      offset = -d.calendar.calculateDuration(new Date(baseStartTs), new Date(oldAnchorTs), offsetUnit);
    }
    proposedAnchorTs = offset === 0
      ? newAnchorTs
      : d.calendar.addDuration(new Date(newAnchorTs), offset, offsetUnit).getTime();
  } else {
    // Backward: offset measured from descendant end back to summary end —
    // signed for the same reason as the forward branch (a descendant may end
    // AFTER the movable tail).
    let offset = 0;
    if (baseEndTs < oldAnchorTs) {
      offset = d.calendar.calculateDuration(new Date(baseEndTs), new Date(oldAnchorTs), offsetUnit);
    } else if (baseEndTs > oldAnchorTs) {
      offset = -d.calendar.calculateDuration(new Date(oldAnchorTs), new Date(baseEndTs), offsetUnit);
    }
    proposedAnchorTs = offset === 0
      ? newAnchorTs
      : d.calendar.addDuration(new Date(newAnchorTs), -offset, offsetUnit).getTime();
  }

  // D-08 block move: every descendant translates by the SAME `delta` ms.
  // Per-descendant working-time snapping is NOT applied — siblings whose
  // proposed positions land on different sides of a non-working boundary
  // (e.g. one on Friday, one on Saturday) would get snapped to different
  // days, destroying their relative offset. v1's `_getImplicitLinks` encodes
  // the original offset in lag and re-derives positions per leaf; v2's D-08
  // chose the simpler bulk-translation model, committing the raw
  // post-translation timestamp even when it lands on a non-working moment.
  //
  // Practical impact: a descendant's stored `start_date` may fall on a
  // weekend after translation. gantt's `calculateEndDate(task)` handles
  // non-working start dates internally (snaps before adding duration), so the
  // displayed bar still spans working time correctly.
  const proposedTs = proposedAnchorTs;

  let startTs: number;
  let endTs: number;
  if (d.kind === "summary") {
    // Nested summary descendant: its stored `duration` is unreliable
    // (project tasks derive their span from children). Preserve the
    // children-derived span it currently holds by shifting BOTH endpoints
    // by the same delta. `aggregateSummaryDates` re-derives it at end-of-pass
    // regardless, but keeping it consistent here avoids transient garbage.
    startTs = baseStartTs + delta;
    endTs = baseEndTs + delta;
  } else if (direction === "forward") {
    startTs = proposedTs;
    endTs = d.duration === 0
      ? proposedTs
      : addDur(d.calendar, proposedTs, d.duration, d.durationUnit);
  } else {
    endTs = proposedTs;
    startTs = d.duration === 0
      ? proposedTs
      : addDur(d.calendar, proposedTs, -d.duration, d.durationUnit);
  }

  // Option A — constraint precedence over the block move (GS-1771 parity).
  // A hard-constrained leaf does NOT translate past its own constraint: we
  // re-apply the constraint bounds (the same `applyConstraintLowerBound` /
  // `applyConstraintUpperBound` the resolvers use) so the leaf clamps back to
  // honor MSO/MFO/FNLT/SNLT/FNET/SNET. The order mirrors the direct-leaf path
  // — asap.ts (forward) applies the lower bound, computes the end, then the
  // upper bound; alap.ts (backward) applies the upper bound, computes the
  // start, then the lower bound — so a summary descendant ends up at the same
  // date it would if scheduled standalone. Unconstrained siblings keep their
  // full-delta translation (the block-move promise holds for what CAN move).
  // Nested summaries aggregate from children, so they have no own constraint
  // to apply here.
  if (d.kind !== "summary" && d.constraintType && d.constraintDateTs !== undefined) {
    ({ startTs, endTs } = applyOptionAClamps(d, startTs, endTs, direction));
  }

  // Strict-calendar mode flags a descendant that lands on its own non-working
  // time (lenient default commits it as-is — see `04-` §11(A)). Evaluated on
  // the FINAL committed anchor (start in forward mode, end in backward) so the
  // flag reflects the post-clamp position.
  const committedAnchorTs = direction === "forward" ? startTs : endTs;
  if (strictCalendar && d.kind !== "summary" && !d.calendar.isWorkingTime(new Date(committedAnchorTs))) {
    pushCalendarNonWorking(
      conflicts,
      d.id,
      new Date(committedAnchorTs),
      new Date(committedAnchorTs)
    );
  }

  const reason: ScheduleReason = { kind: "summary-translation", summaryId };
  plan.set(d.id, { startTs, endTs, reason });
}

function addDur(cal: Calendar, ts: number, duration: number, unit: TimeUnit): number {
  return cal.addDuration(new Date(ts), duration, unit).getTime();
}

/**
 * Option-A constraint re-application shared by `translateDescendant` and
 * `clampDescendantsToSummaryBound`: asap order forward (lower bound on start,
 * recompute end, upper bound on end), alap order backward — identical to the
 * direct-leaf resolvers, so a clamped descendant lands where it would if
 * scheduled standalone.
 */
function applyOptionAClamps(
  d: SchedNode,
  startTs: number,
  endTs: number,
  direction: SnapDirection
): { startTs: number; endTs: number } {
  if (direction === "forward") {
    const ls = applyConstraintLowerBound(startTs, d);
    if (ls !== startTs) {
      startTs = ls;
      endTs = d.duration === 0 ? ls : addDur(d.calendar, ls, d.duration, d.durationUnit);
    }
    const ue = applyConstraintUpperBound(endTs, d);
    if (ue !== endTs) {
      endTs = ue;
      startTs = d.duration === 0 ? ue : addDur(d.calendar, ue, -d.duration, d.durationUnit);
    }
  } else {
    const ue = applyConstraintUpperBound(endTs, d);
    if (ue !== endTs) {
      endTs = ue;
      startTs = d.duration === 0 ? ue : addDur(d.calendar, ue, -d.duration, d.durationUnit);
    }
    const ls = applyConstraintLowerBound(startTs, d);
    if (ls !== startTs) {
      startTs = ls;
      endTs = d.duration === 0 ? ls : addDur(d.calendar, ls, d.duration, d.durationUnit);
    }
  }
  return { startTs, endTs };
}

/**
 * GS-2875 (landing-floor follow-up): the summary's own link landing bounds
 * EVERY leaf of the block — v1 parity. v1's `_getImplicitLinks` explodes a
 * summary-target link into one implicit edge per leaf, so each leaf
 * independently respects the landing as a lower bound under forward
 * scheduling (upper bound backward); the internal links between siblings
 * then act as additional `≥` constraints on top (a leaf sits at
 * `max(landing, ownPredecessor-derived date)`). The v2 block model routes
 * the landing through the summary resolve + delta translation instead,
 * which a leaf tethered to an IN-BLOCK predecessor via a negative lag can
 * escape: its link-derived date legitimately precedes the chain head, but
 * must not precede the summary's landing (`Task #4 = max(Task #2.end,
 * #3.end − 6)` — starting before Task #2's end violates the user-visible
 * FS `2→project` link).
 *
 * Clamps each leaf entry to the bound, then re-applies the leaf's own
 * constraint bounds (option A) so a hard pin still wins over the landing —
 * the same order as the per-leaf resolvers (link candidates first,
 * constraint clamps last). Excluded / completed leaves are fixed-date and
 * are not touched; nested-summary entries re-aggregate from their leaves at
 * end of pass. Runs UNCONDITIONALLY of the translation delta (a delta-0
 * pass still needs the floor: the per-leaf resolve knows nothing about the
 * summary-level link, so a tail re-derived from its in-block predecessor
 * would otherwise drop below the landing and break idempotency).
 */
export function clampDescendantsToSummaryBound(
  graph: SchedulingGraph,
  summaryId: NormalizedTaskID,
  boundTs: number,
  direction: "forward" | "backward",
  plan: Map<NormalizedTaskID, PerNodePlan>
): void {
  for (const c of graph.containment) {
    if (c.summary !== summaryId) continue;
    const d = graph.nodes.get(c.descendant);
    if (!d || d.kind === "summary") continue;
    if (d.excluded || d.isCompleted) continue;
    const entry = plan.get(d.id);
    if (!entry) continue;
    if (direction === "forward" ? entry.startTs >= boundTs : entry.endTs <= boundTs) continue;
    let startTs: number;
    let endTs: number;
    if (direction === "forward") {
      startTs = boundTs;
      endTs = d.duration === 0 ? boundTs : addDur(d.calendar, boundTs, d.duration, d.durationUnit);
    } else {
      endTs = boundTs;
      startTs = d.duration === 0 ? boundTs : addDur(d.calendar, boundTs, -d.duration, d.durationUnit);
    }
    if (d.constraintType && d.constraintDateTs !== undefined) {
      ({ startTs, endTs } = applyOptionAClamps(d, startTs, endTs, direction));
    }
    plan.set(d.id, { startTs, endTs, reason: { kind: "summary-translation", summaryId } });
  }
}

/**
 * Per D-07: after the scheduling pass finishes, summary dates are re-derived
 * from their descendant entries. start = min(descendant starts),
 * end = max(descendant ends). Called from `schedule_pass.ts` at end of pass.
 *
 * Mutates `plan` in place.
 */
export function aggregateSummaryDates(
  graph: SchedulingGraph,
  plan: Map<NormalizedTaskID, PerNodePlan>
): void {
  // Build summary → descendant id index once.
  const descendantsOf = new Map<NormalizedTaskID, NormalizedTaskID[]>();
  for (const c of graph.containment) {
    let arr = descendantsOf.get(c.summary);
    if (!arr) { arr = []; descendantsOf.set(c.summary, arr); }
    arr.push(c.descendant);
  }

  // Aggregate INNER summaries first so OUTER summaries see updated children.
  // Depth = number of containment-ancestors (i.e., how many summaries
  // contain this summary). Highest depth first.
  const depth = new Map<NormalizedTaskID, number>();
  for (const id of descendantsOf.keys()) {
    let d = 0;
    for (const c of graph.containment) {
      if (c.descendant === id) d++;
    }
    depth.set(id, d);
  }
  const sortedSummaries = [...descendantsOf.keys()].sort(
    (a, b) => (depth.get(b) ?? 0) - (depth.get(a) ?? 0)
  );

  for (const summaryId of sortedSummaries) {
    const summary = graph.nodes.get(summaryId);
    if (!summary) continue;
    const descs = descendantsOf.get(summaryId) ?? [];
    let minStart = Number.POSITIVE_INFINITY;
    let maxEnd = Number.NEGATIVE_INFINITY;
    // Aggregate from DIRECT-children-and-leaves only — i.e., the bbox is
    // formed by the immediate plan entries of every descendant in this
    // summary's containment list. (The list already includes transitive
    // descendants from the graph builder, so any descendant's CURRENT plan
    // entry — including a freshly-aggregated inner summary — contributes.)
    for (const d of descs) {
      const entry = plan.get(d);
      if (!entry) continue;
      if (entry.startTs < minStart) minStart = entry.startTs;
      if (entry.endTs > maxEnd) maxEnd = entry.endTs;
    }
    if (isFinite(minStart) && isFinite(maxEnd)) {
      const existing = plan.get(summaryId);
      plan.set(summaryId, {
        startTs: minStart,
        endTs: maxEnd,
        reason: existing?.reason ?? { kind: "preferred" }
      });
    }
  }
}
